import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import Label from '../../../components/label';
import axios from '../../../utils/Axios';
import { currentTimezoneCode } from '../../../utils/timezoneCodes';
import { useAuthContext } from 'src/auth/useAuthContext';

dayjs.extend(utc);
dayjs.extend(timezone);

// The emails of one booking, from GET /bookings/:bookingId/notifications (email plan
// section 3.6). Only accounts in the email upgrade pilot see it. The route (api #225)
// answers `{ items: [{ kind, to, status, state, reason, dueAt, finishedAt }] }`,
// newest first.

const STATUS_EMAILS = {
  APPROVED: 'Booking approved',
  PENDING: 'Booking pending approval',
  REJECTED: 'Booking rejected',
  CANCELED: 'Booking canceled',
};

const STATES = {
  sent: { text: 'Sent', color: 'success' },
  pending: { text: 'Scheduled', color: 'warning' },
  sending: { text: 'Scheduled', color: 'warning' },
  held: { text: 'Waiting for plan renewal', color: 'default' },
  failed: { text: 'Not delivered', color: 'error' },
  canceled: { text: 'Canceled', color: 'default' },
};

const SKIP_REASONS = {
  template_off: 'Off in settings',
  too_late: 'Skipped, too late',
  delivery_off: 'Email sending off',
  not_covered: 'Over plan limit',
};

const LOAD_ERROR = 'Could not load the emails for this booking.';

function emailName(item) {
  if (item.kind === 'booking_status_owner') return 'New booking';
  if (item.kind === 'booking_status_customer') {
    return STATUS_EMAILS[item.status] || 'Booking update';
  }
  if (item.kind?.startsWith('booking_reminder_')) return 'Reminder';
  if (item.kind === 'booking_follow_up_customer') return 'Follow-up';
  return 'Email';
}

function emailResult(item) {
  if (item.state === 'skipped') {
    return { text: SKIP_REASONS[item.reason] || 'Skipped', color: 'default' };
  }
  return STATES[item.state] || { text: 'Unknown', color: 'default' };
}

function isPilot(user) {
  return (
    user?.emailUpgrade?.pilot === true && user?.emailUpgrade?.blocked !== true
  );
}

export default function NotificationHistory({ bookingId }) {
  const { user } = useAuthContext();
  // Return before the list mounts, so an account outside the pilot sends no request.
  if (!isPilot(user)) return null;
  return (
    <EmailList bookingId={bookingId} timezone={currentTimezoneCode(user?.config?.timezone)} />
  );
}

function EmailList({ bookingId, timezone: zone }) {
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true;
    setItems(null);
    setFailed(false);
    axios
      .get(`/bookings/${bookingId}/notifications`)
      .then((res) => {
        if (!current) return;
        const list = res?.data?.items;
        if (Array.isArray(list)) setItems(list);
        else setFailed(true);
      })
      .catch(() => {
        if (current) setFailed(true);
      });
    // Drop the answer when the drawer closes or shows another booking.
    return () => {
      current = false;
    };
  }, [bookingId, attempt]);

  return (
    <>
      <Divider sx={{ my: 2.5 }} />
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        Emails for this booking
      </Typography>
      {failed && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {LOAD_ERROR}
          </Typography>
          <Button type="button" size="small" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </Button>
        </Stack>
      )}
      {!failed && !items && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Loading emails...
        </Typography>
      )}
      {!failed && items?.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No emails yet
        </Typography>
      )}
      {!failed && items?.length > 0 && (
        <Stack spacing={1} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {items.map((item, index) => (
            <EmailRow key={index} item={item} zone={zone} />
          ))}
        </Stack>
      )}
    </>
  );
}

function EmailRow({ item, zone }) {
  const result = emailResult(item);
  const when = item.finishedAt || item.dueAt;
  const details = [
    item.to === 'owner' ? 'to you' : 'to your client',
    when ? dayjs(when).tz(zone).format('D MMM YYYY, h:mm A') : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box
      component="li"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <Stack>
        <Typography variant="body2">{emailName(item)}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {details}
        </Typography>
      </Stack>
      <Label color={result.color} sx={{ flexShrink: 0 }}>
        {result.text}
      </Label>
    </Box>
  );
}
